import { faDistributeSpacingVertical, faEllipsisH, faFilter, faLineColumns, faTableColumns } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress, Theme, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  DataGridPro,
  GridApiRef,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridFeatureMode,
  GridFilterModel,
  GridInitialState,
  GridLinkOperator,
  GridRenderCellParams,
  GridRowIdGetter,
  GridRowParams,
  GridRowsProp,
  GridSelectionModel,
  GridSortModel,
  GridState,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import React, { ReactNode, useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import { MouseoverRenderedTooltip } from './mouseover-rendered-tooltip';

export interface IDataGridProps {
  rows: GridRowsProp;
  columns: GridColDef[];
  hasCheckboxForSelection?: boolean;
  sorting?: GridSortModel;
  disableAutoHeight?: boolean;
  isLoading?: boolean;
  mode: GridFeatureMode;
  rowCount?: number;
  pageSize?: number;
  page?: number;
  columnFilter?: GridFilterModel;
  onSort?: (model: GridSortModel, details: GridCallbackDetails) => void;
  onRowClick?: (params: GridRowParams, event: any) => void;
  onSelectedRow?: (model: GridSelectionModel, details: GridCallbackDetails<any>) => void;
  selectionModel?: GridSelectionModel;
  getActions?: (params: GridRowParams) => JSX.Element[];
  onPageChange?: (page: number, details: GridCallbackDetails) => void;
  onPageSizeChange?: (pageSize: number, details: GridCallbackDetails) => void;
  onFilterModelChange?: (model: GridFilterModel, details: GridCallbackDetails) => void;
  apiRef?: GridApiRef;
  linkOperators?: GridLinkOperator[];
  onFilterPanelOpened?: () => void;
  onFilterPanelClosed?: (model: GridFilterModel) => void;
  getDetailPanelHeight?: (row: any) => number | 'auto';
  getDetailPanelContent?: (row: any) => any;
  disableColumnMenu?: boolean;
  CustomPaginationComponent?: ReactNode;
  rowsPerPageOptions?: number[];
  disablePagination?: boolean;
  enableColumnSelector?: boolean;
  initialState?: GridInitialState;
  onColumnVisibilityModelChange?: (model: Record<string | number | symbol, boolean>) => void;
  onColumnOrderChange?: (params: GridColumnOrderChangeParams) => void;
  onColumnWidthChange?: (params: GridColumnResizeParams, pctColleciton?: { id: string; value: any }[]) => void;
  filterPanelWidth?: number;
  disableToolTip?: boolean;
  getRowId?: GridRowIdGetter;
  keepNonExistentRowsSelected?: boolean;
  isRowSelectable?: (params: GridRowParams) => boolean;
}
export interface StyleProps {
  filterPanelWidth?: number;
}

const useStyles = makeStyles<StyleProps>()((_theme, { filterPanelWidth }) => ({
  root: {
    backgroundColor: 'white',
    color: '#000',
    fontSize: '12px',
  },
  columnHeader: {
    borderRight: '1px solid #ccc',
    '& .MuiSvgIcon-root': {
      width: '.8em',
    },
  },
  overlay: {
    backgroundColor: 'white',
  },
  cell: {
    borderRight: '1px solid #ccc',
  },
  checkboxInput: {
    width: '250px',
  },
  cellCheckbox: {
    '& .MuiSvgIcon-root': {
      width: '.8em',
    },
  },
  filterForm: {
    display: 'flex',
    flexDirection: 'row',
    width: filterPanelWidth ? `${filterPanelWidth}px` : '80vw',
    gap: '10px',
  },
  filterFormColumnInput: {
    flexGrow: 2,
  },
  filterFormOperatorInput: {
    flexGrow: 1,
  },
  filterFormValueInput: {
    flexGrow: 7,
  },
}));

function CustomColumnMenu() {
  return (
    <GridToolbarContainer>
      <Tooltip title="Kolonner">
        <GridToolbarColumnsButton startIcon={<FontAwesomeIcon icon={faTableColumns} />} placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
      </Tooltip>
      <GridToolbarFilterButton placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
      <Tooltip title="Tæthed">
        <GridToolbarDensitySelector
          startIcon={<FontAwesomeIcon icon={faDistributeSpacingVertical} />}
          placeholder=""
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </Tooltip>
    </GridToolbarContainer>
  );
}

export function DataGrid(props: IDataGridProps) {
  const {
    rows,
    columns: inputColumns,
    hasCheckboxForSelection,
    getActions,
    sorting,
    onSort,
    onRowClick,
    onSelectedRow,
    disableAutoHeight,
    isLoading,
    mode,
    rowCount,
    page,
    pageSize,
    selectionModel,
    onPageChange,
    columnFilter,
    onPageSizeChange,
    onFilterModelChange,
    apiRef,
    linkOperators,
    onFilterPanelOpened,
    onFilterPanelClosed,
    getDetailPanelContent,
    getDetailPanelHeight,
    disableColumnMenu,
    CustomPaginationComponent,
    rowsPerPageOptions,
    disablePagination,
    enableColumnSelector,
    initialState,
    onColumnVisibilityModelChange,
    onColumnOrderChange,
    onColumnWidthChange,
    filterPanelWidth,
    disableToolTip,
    getRowId,
    keepNonExistentRowsSelected,
    isRowSelectable: isRowSelectableProp,
  } = props;
  const [filerIsOpen, setFilerIsOpen] = useState<boolean>(false);

  const styleProps = {
    filterPanelWidth,
  };
  const { classes } = useStyles(styleProps);
  const columns = [...inputColumns];
  if (getActions) {
    const actions = {
      field: 'static-actions',
      type: 'actions',
      getActions,
      headerName: 'Handling',
      width: 100,
      editable: false,
      resizable: false,
      datatype: 'none',
    } as GridColDef;
    columns.push(actions);
  }

  const pageSizeCalculated = pageSize || 50;

  const defaultIsRowSelectable = useCallback(() => !!hasCheckboxForSelection, [hasCheckboxForSelection]);
  const isRowSelectableCallback = isRowSelectableProp ?? defaultIsRowSelectable;

  const icon = <FontAwesomeIcon icon={faEllipsisH} />;
  const filterIcon = <FontAwesomeIcon icon={faFilter} />;
  const columnIcon = <FontAwesomeIcon icon={faLineColumns} />;
  function handleCellClick(params: GridCellParams, event: any) {
    if (onRowClick !== undefined) {
      const shouldRowClickBeStopped = params.colDef.field === 'static-actions' || params.colDef.field === '__detail_panel_toggle__' || params.colDef.field === '__check__';
      if (!shouldRowClickBeStopped) {
        const gridRowParams: any = {
          row: params.row,
          id: params.id,
        };
        onRowClick(gridRowParams, event);
      }
    }
  }

  function handleStateChange(state: GridState) {
    if (!onFilterPanelClosed) return;
    if (state.preferencePanel.open && state.preferencePanel.openedPanelValue === 'filters' && !filerIsOpen) {
      flushSync(() => setFilerIsOpen(true));

      if (onFilterPanelOpened) {
        onFilterPanelOpened();
      }
    } else if (!state.preferencePanel.open && filerIsOpen) {
      flushSync(() => setFilerIsOpen(false));
      onFilterPanelClosed(state.filter.filterModel);
    }
  }
  function handleColumtWithChange(params: GridColumnResizeParams) {
    const allColumns = apiRef?.current.getVisibleColumns();
    const totalWidth = allColumns?.reduce((accumulator, obj) => accumulator + obj.computedWidth, 0);
    const pctCollection = allColumns?.map(x => ({ id: x.field, value: (x.computedWidth / (totalWidth ?? 100)) * 100 }));
    onColumnWidthChange && onColumnWidthChange(params, pctCollection);
  }
  function handleSort(model: GridSortModel, details: GridCallbackDetails) {
    if (columns && columns.length > 0) {
      onSort && onSort(model, details);
    }
  }

  const tooltipColumns = columns.map(column => {
    if (column.field && (column.field === 'static-actions' || column.field.startsWith('__'))) {
      return column;
    }
    return {
      ...column,
      renderCell: (params: GridRenderCellParams) => (
        <MouseoverRenderedTooltip title={column.renderCell ? column.renderCell(params) : params.value}>
          <span>{column.renderCell ? column.renderCell(params) : params.value}</span>
        </MouseoverRenderedTooltip>
      ),
    };
  });
  function removeDuplicates(c: GridColDef[]) {
    return c.filter((value, index, self) => index === self.findIndex(t => t.field === value.field));
  }
  return (
    <DataGridPro
      initialState={{
        ...initialState,
        pagination: {
          pageSize: pageSizeCalculated,
        },
      }}
      classes={classes}
      sx={{
        '& .MuiTablePagination-toolbar': {
          backgroundColor: 'white',
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: '#e9edf1',
          cursor: 'pointer',
        },
        '& .MuiDataGrid-root': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeader:focus': {
          outline: 'none',
        },
        '& .MuiDataGrid-cell:focus': {
          outline: 'none',
        },
        '& .MuiBadge-badge': {
          paddingTop: '15px',
        },
        '& .MuiDataGrid-toolbarContainer': {
          '& .MuiBadge-badge': {
            paddingTop: 0,
          },
        },
      }}
      components={{
        MoreActionsIcon: () => icon,
        OpenFilterButtonIcon: () => filterIcon,
        ColumnSelectorIcon: () => columnIcon,
        LoadingOverlay: LinearProgress,
        Pagination: CustomPaginationComponent ? () => CustomPaginationComponent : undefined,
        Toolbar: !disableColumnMenu ? CustomColumnMenu : undefined,
      }}
      componentsProps={{
        filterPanel: {
          linkOperators: linkOperators ?? [GridLinkOperator.And, GridLinkOperator.Or],
        },
        panel: {
          disablePortal: true,
          placement: 'bottom-end',
        },
      }}
      rowHeight={34}
      headerHeight={columns.length > 0 ? 49 : 0}
      keepNonExistentRowsSelected={keepNonExistentRowsSelected}
      disableColumnSelector={!enableColumnSelector}
      disableColumnPinning
      loading={isLoading}
      pagination={!disablePagination}
      density="compact"
      rows={rows}
      columns={disableToolTip ? removeDuplicates(columns) : removeDuplicates(tooltipColumns)}
      isRowSelectable={isRowSelectableCallback}
      rowsPerPageOptions={rowsPerPageOptions || [pageSizeCalculated]}
      page={page}
      pageSize={page !== undefined ? pageSize : undefined}
      selectionModel={selectionModel}
      checkboxSelection={hasCheckboxForSelection}
      disableSelectionOnClick={hasCheckboxForSelection}
      onColumnOrderChange={onColumnOrderChange}
      autoHeight={!disableAutoHeight}
      sortModel={sorting?.filter(x => columns.some(c => c.field === x.field))}
      sortingMode={mode}
      columnBuffer={columns.length}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      paginationMode={mode}
      rowCount={rowCount}
      filterMode={mode}
      onPageSizeChange={onPageSizeChange}
      onSortModelChange={handleSort}
      onSelectionModelChange={onSelectedRow}
      onPageChange={onPageChange}
      onCellClick={handleCellClick}
      onFilterModelChange={onFilterModelChange}
      filterModel={columnFilter}
      onStateChange={state => handleStateChange(state)}
      apiRef={apiRef}
      getDetailPanelHeight={getDetailPanelHeight}
      getDetailPanelContent={getDetailPanelContent}
      disableColumnMenu={disableColumnMenu}
      onColumnWidthChange={handleColumtWithChange}
      getRowId={getRowId}
    />
  );
}
